import ProductAttributesApiV2 from 'api/v2/admins/product-attributes-api-v2'
import { SelectSearchField } from 'components'
import { useField } from 'formik'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { limitFilter } from 'utils/generator-utils'
import { STATUS_OPTION } from 'domains/product-attribute/constants'

const propTypes = {
  code       : PropTypes.string,
  name       : PropTypes.string,
  label      : PropTypes.string,
  required   : PropTypes.bool,
  isCuration : PropTypes.bool
}

const defaultProps = {
  isCuration: false
}


/**
 * Render Dynamic Select Field. It made from `SelectSearchField`
 * @param {string} code - Product Attribute code
 * @param {string} name - Field name in the formik bag
 * @param {string} label - Product Attribute label
 * @param {string} required - To flag the required field
 * @param {bool} isCuration - Set true if it's for curation form
 */
const ProductAttributeSelectSearchField = ({
  code,
  name,
  label,
  required,
  isCuration,
  ...props
}) => {
  const [ field ]                           = useField(name)
  const [ initialOption, setInitialOption ] = useState()

  const [ options, setOptions ]     = useState([])
  const [ isLoading, setIsLoading ] = useState(true)

  const { value } = field

  useEffect(() => {
    if (options.length > 0) return

    const optionsFormCache = sessionStorage.getItem(code)

    if (optionsFormCache) {
      setIsLoading(false)
      return
    }

    const optionEndpoint = isCuration
      ? ProductAttributesApiV2.getCurationOptions({ attributeCode: code })
      : ProductAttributesApiV2.getOptions({ attributeCode: code, additionalFilters: { status: STATUS_OPTION.ENABLED } })

    optionEndpoint.then((response) => {
      const options = response.data.options

      // setOptions(options)
      setIsLoading(false)
      sessionStorage.setItem(code, JSON.stringify(options))
    })
      .catch(() => {
        alert(`Failed: Get Attribute ${code}`)
      })
  }, [ code, isCuration, options.length ])


  useEffect(() => {
    const optionsFormCache = sessionStorage.getItem(code)

    if (optionsFormCache && value && !isLoading) {
      const jsonOptions = JSON.parse(optionsFormCache)

      const selectedOption = jsonOptions.find((option) => option.id === Number(value))
      setInitialOption(selectedOption)
    }
  }, [ code, isLoading, value ])

  /**
   * Render option label
   * @param {object} option - field option
   */
  const getOptionLabel = (option) => (
    <>
      <span className='mr-3'>{ option.label }</span>
    </>
  )

  /**
   * Get the custom value in formigBag.values
   * @param {object} option - category option
   */
  const getOptionValue = (option) => option.id

  /**
   * Handle searching options
   * @param {object} event - event
   */
  const handleSelectSearchOnChange = (event) => {
    const searchText = event.target.value.toLowerCase()

    if (searchText === '') {
      setOptions([])
      return
    }

    const optionsFormCache = sessionStorage.getItem(code)

    if (optionsFormCache) {
      const jsonOptions = JSON.parse(optionsFormCache)

      const filteredOptions = Array.from(limitFilter(jsonOptions, (option) => option.label.toLowerCase().startsWith(searchText), 20))
      setOptions(filteredOptions)
    }
  }


  return (
    <SelectSearchField
      type='text'
      name={ name }
      label={ label }
      initialOption={ initialOption }
      placeholder={ `Search ${label}` }
      dataList={ options }
      onSearchChange={ handleSelectSearchOnChange }
      getOptionLabel={ getOptionLabel }
      getOptionValue={ getOptionValue }
      isLoading={ isLoading }
      required={ required }
      // turn of auto suggest from the input field
      autoComplete='off'
      autoCorrect='off'
      spellCheck='off'
      { ...props }
    />
  )

}

ProductAttributeSelectSearchField.propTypes    = propTypes
ProductAttributeSelectSearchField.defaultProps = defaultProps

export default ProductAttributeSelectSearchField
